import type { CashflowRow } from '../../types/api';
import { usd } from '../../lib/format';
import { extent, linear, niceTicks } from './scale';

/** Annual free cash flow bars with the cumulative position as a line. */
export function CashflowChart({ rows }: { rows: CashflowRow[] }) {
  const W = 680;
  const H = 240;
  const m = { l: 56, r: 12, t: 12, b: 26 };
  const values = rows.flatMap((r) => [r.fcf, r.cumulative_fcf, 0]);
  const [lo, hi] = extent(values);
  const y = linear([lo, hi], [H - m.b, m.t]);
  const band = (W - m.l - m.r) / rows.length;
  const x = (i: number) => m.l + i * band;
  const ticks = niceTicks(lo, hi, 5);
  const line = rows.map((r, i) => `${i ? 'L' : 'M'}${x(i) + band / 2},${y(r.cumulative_fcf)}`).join(' ');
  const payback = rows.findIndex((r, i) => i > 0 && r.cumulative_fcf >= 0 && rows[i - 1].cumulative_fcf < 0);

  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Annual free cash flow and cumulative cash position">
      <g className="grid">{ticks.map((t) => <line key={t} x1={m.l} x2={W - m.r} y1={y(t)} y2={y(t)} />)}</g>
      {ticks.map((t) => <text key={t} x={m.l - 6} y={y(t) + 3} textAnchor="end">{usd(t)}</text>)}
      <line className="zero" x1={m.l} x2={W - m.r} y1={y(0)} y2={y(0)} />
      {rows.map((r, i) => {
        const top = Math.min(y(r.fcf), y(0));
        const h = Math.max(1, Math.abs(y(r.fcf) - y(0)));
        return (
          <rect key={r.year} x={x(i) + band * 0.18} width={band * 0.64} y={top} height={h} rx={1.5}
            fill={r.phase === 'construction' ? 'var(--s-capital)' : r.fcf >= 0 ? 'var(--s-pos)' : 'var(--s-neg)'}>
            <title>{`Year ${r.year} (${r.phase}): FCF ${usd(r.fcf)}, cumulative ${usd(r.cumulative_fcf)}`}</title>
          </rect>
        );
      })}
      <path d={line} fill="none" stroke="var(--navy)" strokeWidth={1.8} />
      {payback > 0 && (
        <g>
          <line x1={x(payback) + band / 2} x2={x(payback) + band / 2} y1={m.t} y2={H - m.b} stroke="var(--amber)" strokeDasharray="3 3" />
          <text x={x(payback) + band / 2 + 4} y={m.t + 10} style={{ fill: 'var(--amber)' }}>payback</text>
        </g>
      )}
      {rows.map((r, i) => (i % 2 === 0 ? <text key={r.year} x={x(i) + band / 2} y={H - 8} textAnchor="middle">{r.year}</text> : null))}
    </svg>
  );
}
